import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  CounterQuestionConfig,
  CounterType,
  DateTypeFilter,
  formatDateLocalized,
  getFullName,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useFilteredWorkspaceRoutines,
  useFindByQuery,
  WorkflowRoutineConfig,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

type Props = {
  label: string;
  routineIds: string[];
  workspaceId: string;
  dateFilter?: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
  };
  hideButton?: boolean;
};

type CSVData = {
  headers: any[];
  rows: any[];
  filename: string;
};

export const DownloadRoutine = forwardRef<
  { triggerDownload: () => void },
  Props
>((props, ref) => {
  const {
    label,
    routineIds,
    workspaceId,
    dateFilter,
    hideButton = false,
  } = props || {};
  const { t } = useTranslation();
  const csvRefs = useRef<(any | null)[]>([]);
  const [csvDataList, setCsvDataList] = useState<CSVData[]>([]);
  const [doExport, toggleExport] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentDownloadIndex, setCurrentDownloadIndex] = useState(0);
  const [uniqueVersionIds, setUniqueVersionIds] = useState<string[]>([]);

  // Expose download function via ref
  useImperativeHandle(ref, () => ({
    triggerDownload: onDownload,
  }));

  const routines = useFilteredWorkspaceRoutines(
    { id: { in: routineIds } },
    workspaceId
  );
  const getDateFilter = () => {
    if (!dateFilter || dateFilter.dateType === DateTypeFilter.ALL) {
      return {};
    }

    if (dateFilter.dateType === DateTypeFilter.CUSTOMDATE) {
      const filter: any = {};
      if (dateFilter.startDate) {
        // Send actual UTC time
        const startDate = new Date(dateFilter.startDate);
        filter.ge = startDate.toISOString();
      }
      if (dateFilter.endDate) {
        // Send actual UTC time at end of day
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        filter.le = endDate.toISOString();
      }
      return filter;
    }

    const now = new Date();
    let startDate: Date;
    const endDate: Date = new Date(now);

    switch (dateFilter.dateType) {
      case DateTypeFilter.TODAY:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate.setHours(0, 0, 0, 0);
        break;

      case DateTypeFilter.THISWEEK:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;

      case DateTypeFilter.THISMONTH:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;

      default:
        return {};
    }
    endDate.setHours(23, 59, 59, 999);

    return {
      ge: startDate.toISOString(),
      le: endDate.toISOString(),
    };
  };

  const { data } = useCustomQuery({
    query: GET_ROUTINE_DATA,
    cacheKey: ['exportRoutineData', routineIds, dateFilter],
    variables: {
      filter: {
        routine: { in: routineIds },
        ...(Object.keys(getDateFilter()).length > 0 && {
          createdAt: getDateFilter(),
        }),
        isDraft: { ne: true },
      },
      orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
    },
    options: {
      enabled: doExport,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  // Extract unique version IDs from the data
  const [versionIdsExtracted, setVersionIdsExtracted] = useState(false);

  useEffect(() => {
    if (!data) {
      setVersionIdsExtracted(false);
      return;
    }

    const versionIds = new Set<string>();
    data.findAssetRoutines.items.forEach((item: any) => {
      if (item.routineVersion?.id) {
        versionIds.add(item.routineVersion.id);
      }
    });

    const uniqueIds = Array.from(versionIds);
    setUniqueVersionIds(uniqueIds);
    setVersionIdsExtracted(true);
  }, [data]);

  const { data: versionData, isLoading: isVersionsLoading } = useFindByQuery({
    query: GET_ROUTINE_VERSIONS,
    modelName: 'workspaceRoutineVersion',
    variables: {
      filter: {
        id: { in: uniqueVersionIds },
      },
    },
    options: {
      enabled: uniqueVersionIds.length > 0,
    },
  });

  const versionConfigsMap = useMemo(() => {
    const map = new Map<string, any>();
    if (versionData?.items) {
      versionData.items.forEach((version: any) => {
        map.set(version.id, version);
      });
    }
    return map;
  }, [versionData]);

  const baseHeaders = useMemo(
    () => [
      { label: 'Asset', key: 'asset' },
      { label: 'Created By', key: 'createdBy' },
      { label: 'Created Date', key: 'createdDate' },
      { label: 'Created Time', key: 'createdTime' },
      { label: 'Groups', key: 'groups' },
      { label: 'Is Edit', key: 'isEdit' },
    ],
    []
  );

  const createCounterColumns = useCallback(
    (baseLabel: string, questionId: string) => {
      return [
        { label: `${baseLabel} - Starting Value`, key: `${questionId}_start` },
        { label: `${baseLabel} - Delta`, key: `${questionId}_delta` },
        { label: `${baseLabel} - Ending Value`, key: questionId },
      ];
    },
    []
  );

  const formatCounterData = useCallback((response: any) => {
    const count = response.count ?? 0;
    const userCount = response.userCount ?? 0;
    const startingValue = count - userCount;
    
    return {
      [`${response.questionId}_start`]: startingValue.toString(),
      [`${response.questionId}_delta`]: userCount.toString(),
      [response.questionId]: count.toString(),
    };
  }, []);

  const onDownload = () => {
    if (csvDataList?.length > 0) {
      // user already clicked on icon 1x, so just download it
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    } else {
      toggleExport(true);
      // setLabel('exporting');
    }
  };

  const downloadNextFile = useCallback(() => {
    if (currentDownloadIndex >= csvDataList.length) {
      // All files downloaded
      toggleExport(false);
      setCurrentDownloadIndex(0);
      return;
    }

    setTimeout(() => {
      csvRefs.current[currentDownloadIndex]?.link?.click();
      setCurrentDownloadIndex((prev) => prev + 1);
    }, 100);
  }, [currentDownloadIndex, csvDataList.length]);

  // Trigger next download when currentDownloadIndex changes
  useEffect(() => {
    if (
      doExport &&
      csvDataList.length > 1 &&
      currentDownloadIndex > 0 &&
      currentDownloadIndex < csvDataList.length
    ) {
      downloadNextFile();
    }
  }, [currentDownloadIndex, doExport, csvDataList.length, downloadNextFile]);

  const getBaseRowData = useCallback((item: any) => {
    return {
      asset: item.asset.name,
      createdBy: getFullName(item.createdBy.name),
      createdDate: item.createdAt
        ? new Date(item.createdAt).toISOString().split('T')[0]
        : '', // YYYY-MM-DD
      createdTime: item.createdAt
        ? new Date(item.createdAt).toISOString().split('T')[1].substring(0, 8)
        : '', // HH:MM:SS
      groups:
        item.workspaceAsset.groups?.length > 0
          ? item.workspaceAsset.groups
              .filter((g: any) => g && g.name)
              .map((g: any) => g.name)
              .join(', ')
          : '',
      isEdit: item.data?.auditLog?.auditLog?.length > 0 ? 'Yes' : '',
    };
  }, []);

  const getResponseKey = useCallback((response: any) => {
    const keys = Object.keys(response);
    const valueKeys = [
      'value',
      'confirmedValue',
      'count',
      'status',
      'option',
      'options',
      'mediaIds',
      'timeTypeValue',
    ];
    return keys.find((key) => valueKeys.includes(key));
  }, []);

  const createWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      // Collect all unique questions from all versions in the data
      const allQuestionsMap = new Map<
        string,
        { id: string; instructions: string; order: number }
      >();
      const multiColumnCounterIds = new Set<string>();

      items.forEach((item) => {
        const versionId = item.routineVersion?.id;
        const versionData = versionId ? versionConfigsMap.get(versionId) : null;

        if (versionData?.config?.pages) {
          const versionConfig = versionData.config as WorkflowRoutineConfig;
          versionConfig.pages.forEach((page) => {
            page.questions?.forEach((question) => {
              allQuestionsMap.set(question.id, {
                id: question.id,
                instructions: question.instructions || '',
                order: question.order,
              });

              if (question.type === 'COUNTER') {
                const counterType = (question as CounterQuestionConfig)
                  .counterType;
                if (
                  counterType === CounterType.Cumulative ||
                  counterType === CounterType.Tally
                ) {
                  multiColumnCounterIds.add(question.id);
                }
              }
            });
          });
        }
      });

      // Sort questions by order to maintain some consistency
      const allQuestions = Array.from(allQuestionsMap.values()).sort(
        (a, b) => a.order - b.order
      );

      const headers = [...baseHeaders];
      allQuestions.forEach((question) => {
        if (multiColumnCounterIds.has(question.id)) {
          headers.push(
            ...createCounterColumns(question.instructions, question.id)
          );
        } else {
          headers.push({ label: question.instructions, key: question.id });
        }
      });

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        const row: any = {
          ...baseRowData,
        };

        // Map responses to question columns
        item.data.responses.forEach((response: any) => {
          if (multiColumnCounterIds.has(response.questionId)) {
            Object.assign(row, formatCounterData(response));
          } else {
            const valueKey = getResponseKey(response);
            row[response.questionId] = formatResponseValue(response, valueKey);
          }
        });

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [
      baseHeaders,
      getBaseRowData,
      formatResponseValue,
      getResponseKey,
      formatCounterData,
      createCounterColumns,
    ]
  );

  const createNonWorkflowData = useCallback(
    (items: any[], workspaceRoutine: WorkspaceRoutine): CSVData => {
      const config = workspaceRoutine.config as any;
      const isMultiColumn =
        workspaceRoutine.type === RoutineType.Counter &&
        (config?.counterType === CounterType.Cumulative ||
          config?.counterType === CounterType.Tally);

      const headers = [...baseHeaders];

      if (isMultiColumn) {
        headers.push(
          {
            label: `${workspaceRoutine.config.title} - Starting Value`,
            key: 'startingValue',
          },
          { label: `${workspaceRoutine.config.title} - Delta`, key: 'delta' },
          {
            label: `${workspaceRoutine.config.title} - Ending Value`,
            key: 'data',
          }
        );
      } else {
        headers.push({ label: workspaceRoutine.config.title, key: 'data' });
      }

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        let data = '';

        switch (workspaceRoutine.type) {
          case RoutineType.Status:
            // Status routine: use status value, or openEndedResponse if user typed custom value
            if (
              item.data.status === 'openEndedResponse' &&
              item.data.openEndedResponse
            ) {
              data = item.data.openEndedResponse;
            } else {
              data = item.data.status || '';
            }
            break;
          case RoutineType.Counter:
            // Counter routine: use count as the value (for Simple it's the only value, for Cumulative/Tally it's the ending value)
            data = item.data.count?.toString() ?? '';
            break;
          case RoutineType.QuickConfirm:
            // QuickConfirm routine: use confirmedValue
            data = item.data.confirmedValue || '';
            break;
          default:
            // Fallback for any other types - grab first value from data object
            const values = Object.values(item.data).filter(
              (v) => v !== undefined && v !== null
            );
            data = values.length > 0 ? String(values[0]) : '';
        }

        const row: any = { ...baseRowData, data };

        if (isMultiColumn) {
          const count = item.data.count ?? 0;
          const userCount = item.data.userCount ?? 0;
          const startingValue = count - userCount;
          
          row.startingValue = startingValue.toString();
          row.delta = userCount.toString();
        }

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [baseHeaders, getBaseRowData]
  );

  useEffect(() => {
    if (!data || !versionIdsExtracted) {
      return;
    }

    if (
      uniqueVersionIds.length > 0 &&
      (isVersionsLoading || versionConfigsMap.size === 0)
    ) {
      return;
    }

    const newCsvDataList: CSVData[] = [];

    routines.forEach((routine) => {
      const routineItems = data.findAssetRoutines.items.filter(
        (item: any) => item.routine.id === routine.id
      );

      if (routineItems.length > 0) {
        if (routine.type === RoutineType.Workflow) {
          newCsvDataList.push(createWorkflowData(routineItems, routine));
        } else {
          newCsvDataList.push(createNonWorkflowData(routineItems, routine));
        }
      }
    });

    setCsvDataList(newCsvDataList);
  }, [
    data,
    routines,
    createWorkflowData,
    createNonWorkflowData,
    uniqueVersionIds,
    versionConfigsMap,
    isVersionsLoading,
    versionIdsExtracted,
  ]);

  // Separate effect for triggering download when doExport changes
  useEffect(() => {
    if (doExport && csvDataList.length > 0) {
      if (csvDataList.length === 1) {
        // Single file download
        setTimeout(() => {
          csvRefs.current[0]?.link?.click();
          toggleExport(false);
          setCurrentDownloadIndex(0);
        }, 50);
      } else {
        // Multiple files download - download them sequentially
        setCurrentDownloadIndex(0);
        downloadNextFile();
      }
    }
  }, [doExport, csvDataList.length]);

  return (
    <>
      {!hideButton && (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={({ pressed }) => [
            styles.customButton,
            styles.buttonStyle,
            isHovered && styles.hovered,
            pressed && styles.pressed,
          ]}
          onPress={onDownload}
          disabled={doExport}
        >
          <View style={{ flexDirection: 'row' }}>
            <MaterialCommunityIcons
              name="download"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            <Text style={styles.label}>{label}</Text>
          </View>
        </Pressable>
      )}

      {/* Render CSV links for each routine */}
      {csvDataList.map((csvData, index) => (
        <CSVLink
          key={index}
          style={{ display: 'none' }}
          ref={(el: any) => (csvRefs.current[index] = el)}
          data={csvData.rows}
          headers={csvData.headers}
          filename={csvData.filename}
        />
      ))}
    </>
  );
});

const styles = StyleSheet.create({
  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: -0.25,
  },
  customButton: {
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderRadius: 8,
  },
  hovered: {
    backgroundColor: Colors.primaryGrayHeader,
  },
  pressed: {
    backgroundColor: Colors.blackWithAlpha,
  },
  buttonStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 128,
    height: 32,
    backgroundColor: Colors.white,
    borderRadius: 16,
  },
});

const GET_ROUTINE_DATA = `
  query FindAssetRoutines($filter: AssetRoutineFilter, $orderBy: OrderByInput) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        routine {
          id
        }
        routineVersion {
          id
        }
        asset {
          name
        }
        workspaceAsset {
          groups {
            name
          }
        }
        createdAt
        createdBy {
          name {
            first
            last
          }
        }
        data
      }
    }
  }
`;

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        id
        type
        config
      }
    }
  }
`;

function formatResponseValue(response: any, valueKey?: string) {
  const value = response[valueKey as string];

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (value && valueKey === 'timeTypeValue') {
    if (
      typeof value === 'object' &&
      'startTime' in value &&
      'endTime' in value
    ) {
      return `${formatDateLocalized(value.startTime)} - ${formatDateLocalized(
        value.endTime
      )}`;
    } else if (typeof value === 'string') {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return formatDateLocalized(value);
      }
      return value;
    }
  } else if (
    valueKey === 'status' &&
    value === 'openEndedResponse' &&
    response.openEndedResponse
  ) {
    return response.openEndedResponse;
  }

  return value;
}
